import { helpHref } from './sections'

/**
 * Which guide explains each app page: the "Learn more" links (`<LearnMore route="/schedule" />`), the ? menu's
 * "Help for this page", and the freshness check (scripts/help-freshness.ts). Owner: slice 36 `web-help-centre`;
 * slice 37 `help-content` adds the rest of the routes as it writes their guides.
 *
 * Keys are TanStack route paths without a trailing slash ("/queue/$lane"); values are guide ids ("<section>/<slug>").
 * The help tests fail if a key is not a real route or a value is not a visible guide.
 */
export const HELP_FOR_ROUTE = {
  '/': 'queue-and-cases/home-and-today',
  '/queue/$lane': 'queue-and-cases/work-the-queue',
  '/case/$id': 'queue-and-cases/the-case-page',
  '/followups': 'queue-and-cases/the-follow-ups-screen',
  '/schedule': 'dispatch-and-billing/the-schedule',
  '/dispatch': 'dispatch-and-billing/the-schedule',
  '/admin/people': 'admin/manage-people',
  '/admin/ai-spend': 'admin/ai-spend',

  // --- help-content: contractor onboarding and Desk transition (#37/#44) ---
  '/connect-desk': 'working-in-desk/what-moved-to-patchbay',

  // --- help-portal: public links, contractor work and phone hand-offs (#37) ---
  '/portal/link': 'contractor-portal/open-the-job-app',
  '/portal': 'contractor-portal/getting-a-booking',
  '/portal/jobs/$id': 'contractor-portal/read-your-work-packet',
  '/w': 'contractor-portal/open-your-job-link',
  '/w/$token': 'contractor-portal/open-your-job-link',
  '/availability/$token': 'contractor-portal/send-your-availability',
  '/pick/$token': 'contractor-portal/pick-a-visit-time',
  '/visit-outcome/$visitId': 'contractor-portal/send-visit-details',
  '/survey': 'contractor-portal/rate-your-experience',
  '/receive/$orderId': 'dispatch-and-billing/receive-hardware',

  // --- help-content: staff money and ledger (#37/#44) ---
  '/billing': 'dispatch-and-billing/create-the-customer-invoice',
  '/ledger': 'dispatch-and-billing/ledger-lanes',
  '/ledger/payment-owed': 'dispatch-and-billing/review-payment-owed',
  '/ledger/procurement': 'dispatch-and-billing/track-procurement',
  '/profit': 'dispatch-and-billing/read-profitability',
  '/admin/rates': 'admin/manage-rates',
  '/admin/settings': 'admin/settings-and-rollouts',

  // --- help-content: screens that had no page of their own (#44) ---
  '/admin/integrations': 'admin/integrations-health',
  '/admin/audit': 'admin/audit-log',
  '/admin/snippets': 'admin/manage-snippets',
  '/admin/riley': 'admin/riley-operations',
  '/admin/contractors': 'admin/contractor-directory',
  '/admin/removed': 'admin/restore-removed-cases',

  // --- help-pages: remaining staff app screens (#37) ---
  '/queue': 'queue-and-cases/work-the-queue',
  '/cases/all': 'queue-and-cases/all-cases',
  '/cases/open': 'queue-and-cases/all-open-cases',
  '/cases/closed': 'queue-and-cases/closed-cases',
  '/accounts/$id': 'queue-and-cases/customer-account',
  '/contacts/$id': 'queue-and-cases/customer-contact',
  '/knowledge': 'getting-started/use-knowledge',
  '/notifications': 'getting-started/notifications',
  '/me': 'getting-started/personal-settings',
  '/my-day': 'queue-and-cases/my-day',
  '/admin': 'admin/people-and-roles',
  '/reports/$reportId': 'riley/saved-reports',
  '/satisfaction': 'admin/read-satisfaction',

  // --- projects help (#1388 slice 97) ---
  // /admin/projects-import is not a route file yet, so it stays out of this map.
  '/projects': 'projects/the-pm-morning',
  '/projects/$lane': 'projects/the-pm-morning',
  '/project/$id': 'projects/the-project-page',
  '/admin/project-templates': 'projects/project-templates',
} as const satisfies Record<string, string>

export type HelpRoute = keyof typeof HELP_FOR_ROUTE

/** Guides for moments rather than pages: `<LearnMore topic="from-desk" />`. */
export const HELP_FOR_TOPIC = {
  /** "You can keep working in Desk": the sign-in screen and the Connect Desk screen. */
  'from-desk': 'working-in-desk/keep-working-in-desk',
  /** The Riley rail's welcome and the palette's Ask mode. */
  riley: 'riley/ask-riley',
  /** The contractor portal's ? menu (slice 35). Public. */
  'contractor-portal': 'contractor-portal/open-your-job-link',
  /** Queue → Proposals. The lane shares the /queue/$lane route, so it is not a HELP_FOR_ROUTE key. */
  proposals: 'queue-and-cases/the-proposals-lane',
  /** The Riley tab on a case: summary, pending cards, full-size. */
  'riley-rail': 'riley/the-riley-rail',
  /** The global top-bar work timer (#1014). It has no route of its own. */
  'work-timer': 'dispatch-and-billing/the-work-timer',
  /** Click-to-call on a case (#509). It has no route of its own. */
  'click-to-call': 'queue-and-cases/click-to-call',

  // --- help-features: recently shipped case and portal flows (#37) ---
  'tech-reminders': 'working-in-desk/answer-a-visit-reminder',
  'teammate-follow-up': 'queue-and-cases/follow-up-with-a-teammate',
  'set-customer': 'queue-and-cases/set-the-customer-and-contact',
  'case-threads': 'queue-and-cases/internal-and-external-threads',
  'off-channel-updates': 'queue-and-cases/add-off-channel-updates',
  'case-linked-records': 'queue-and-cases/linked-hardware-and-deals',
  'reload-after-update': 'getting-started/reload-after-an-update',
  'contractor-documentation': 'dispatch-and-billing/contractor-documentation',

  /** Profitability ▸ Projects. The tab shares /profit with the statement, so it is not a HELP_FOR_ROUTE key. */
  'profit-projects': 'projects/profit-projects',
} as const satisfies Record<string, string>

export type HelpTopic = keyof typeof HELP_FOR_TOPIC

export function helpForTopic(topic: HelpTopic, anchor?: string): string {
  return helpHref(HELP_FOR_TOPIC[topic], anchor)
}

export function helpForRoute(route: HelpRoute, anchor?: string): string {
  return helpHref(HELP_FOR_ROUTE[route], anchor)
}

/**
 * The guide for a concrete pathname ("/queue/reply-owed" -> the "/queue/$lane" guide), for the ? menu. Matches
 * segment by segment, `$param` matching any one segment; null when no route in the map matches.
 */
export function helpForPathname(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/'
  // Proposals is a queue lane, not its own route file, so /queue/$lane would otherwise win.
  if (clean === '/queue/proposals') return helpHref(HELP_FOR_TOPIC.proposals)
  // The six Reports ▸ Projects ids share /reports/$reportId with saved Riley reports.
  if (/^\/reports\/project_(portfolio|ageing|forecast|hardware|margin|workload)$/.test(clean)) {
    return helpHref('projects/project-reports')
  }
  const segs = clean.split('/').filter(Boolean)
  for (const [route, id] of Object.entries(HELP_FOR_ROUTE)) {
    const rsegs = route.split('/').filter(Boolean)
    if (rsegs.length !== segs.length) continue
    if (rsegs.every((r, i) => r.startsWith('$') || r === segs[i])) return helpHref(id)
  }
  return null
}

/** Names for the "In the app" links on a guide (frontmatter `routes`); dynamic routes get no link. */
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Home',
  '/queue': 'Queue',
  '/cases/all': 'All cases',
  '/cases/open': 'All open',
  '/cases/closed': 'Closed cases',
  '/accounts/$id': 'Customer account',
  '/contacts/$id': 'Contact',
  '/schedule': 'Schedule',
  '/dispatch': 'Schedule',
  '/billing': 'Ready to bill',
  '/ledger': 'Ledger',
  '/knowledge': 'Knowledge',
  '/admin': 'Admin',
  '/admin/people': 'People',
  '/admin/rates': 'Rates',
  '/admin/settings': 'Settings',
  '/admin/integrations': 'Integrations',
  '/admin/audit': 'Audit log',
  '/admin/snippets': 'Snippets',
  '/admin/riley': 'Riley ops',
  '/admin/contractors': 'Contractors',
  '/admin/removed': 'Removed cases',
  '/admin/ai-spend': 'AI spend',
  '/followups': 'Follow-ups',
  '/portal': 'My jobs',
  '/w': 'Open job link',
  '/survey': 'Satisfaction survey',
  '/ledger/procurement': 'Procurement',
  '/me': 'Me',
  '/my-day': 'My day',
  '/notifications': 'Notifications',
  '/reports/$reportId': 'Saved report',
  '/satisfaction': 'Satisfaction',
  '/receive/$orderId': 'Receive hardware',
  '/visit-outcome/$visitId': 'Visit outcome',
  '/projects': 'Projects',
  '/projects/$lane': 'Project lane',
  '/project/$id': 'Project',
  '/admin/project-templates': 'Project templates',
  '/admin/projects-import': 'Projects import',
}

export function appRouteLabel(route: string): string | null {
  // A literal "$id" is not a usable link. Keep dynamic-route labels in the registry for navigation and coverage,
  // but show "In the app" only for paths a guide can open directly.
  return route.includes('$') ? null : (ROUTE_LABELS[route] ?? null)
}
